import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaFacebookF, FaTiktok, FaSnapchatGhost, FaLinkedinIn, FaGoogle, FaSyncAlt } from "react-icons/fa";
import { getRequest, postRequest } from "../../../../services/api/apiService";

// ── Panneau « Synchronisation & connexion par token » ─────────────
// 1. Sync À LA DEMANDE par régie : plus besoin d'attendre le batch (5 min).
//    L'état running/erreur/dernier succès vient du serveur (/sync/live-status),
//    on ne devine rien côté client.
// 2. Connexion PAR TOKEN : alternative à l'OAuth quand celui-ci est bloqué
//    (app Partner indisponible, profil sans Business Manager, accès agence).
//    Le token est généré par l'utilisateur dans SA régie, validé en direct,
//    et reste révocable de son côté à tout moment.

const STYLE = {
  meta:     { label: "Meta", icon: <FaFacebookF />,     bg: "#1877f2", fg: "#fff" },
  tiktok:   { label: "TikTok", icon: <FaTiktok />,      bg: "#010101", fg: "#fff" },
  snapchat: { label: "Snapchat", icon: <FaSnapchatGhost />, bg: "#FFFC00", fg: "#1a1a1a" },
  linkedin: { label: "LinkedIn", icon: <FaLinkedinIn />, bg: "#0a66c2", fg: "#fff" },
  google:   { label: "Google", icon: <FaGoogle />,       bg: "#4285f4", fg: "#fff" },
};

// Où l'utilisateur génère son token, par régie (texte affiché sous le champ).
const TOKEN_HELP = {
  meta: "Business Manager → Paramètres de l'entreprise → Utilisateurs système → votre utilisateur → « Générer un nouveau token » (permissions ads_management + ads_read). Compte : act_123456789.",
  tiktok: "TikTok Business Center → Développeur → votre app → générer un long-lived Access Token. Compte : l'advertiser_id (chiffres).",
  snapchat: "Snapchat Business → Paramètres → OAuth Apps → générer un access token. Compte : l'ID du compte publicitaire (UUID).",
  linkedin: "LinkedIn Developers → votre app → token avec r_ads/rw_ads. Compte : l'ID numérique du compte Campaign Manager.",
};

const TOKEN_PLATFORMS = ["meta", "tiktok", "snapchat", "linkedin"];

const fmtDate = (d) => {
  if (!d) return "jamais";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "—" : t.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const SyncAndTokenPanel = () => {
  const [status, setStatus] = useState({ platforms: [], anyRunning: false });
  const [busy, setBusy] = useState(null);
  const [tokenForm, setTokenForm] = useState({ platform: "meta", accessToken: "", adAccountId: "" });
  const [connecting, setConnecting] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getRequest("/api/sync/live-status");
      setStatus({ platforms: res.platforms || [], anyRunning: !!res.anyRunning });
    } catch { /* silencieux : le panneau reste utilisable */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tant qu'une synchro tourne, on rafraîchit l'état toutes les 3 s.
  useEffect(() => {
    if (!status.anyRunning) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [status.anyRunning, load]);

  const runSync = async (platform) => {
    setBusy(platform || "all");
    try {
      await postRequest(`/api/sync/trigger${platform ? `?platform=${platform}` : ""}`, {});
      toast.info(platform ? `Synchronisation ${STYLE[platform]?.label || platform} lancée…` : "Synchronisation lancée…");
      setTimeout(load, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Échec du lancement de la synchronisation.");
    } finally {
      setBusy(null);
    }
  };

  const connectToken = async () => {
    const { platform, accessToken, adAccountId } = tokenForm;
    if (!accessToken.trim() || !adAccountId.trim()) return;
    setConnecting(true);
    try {
      const res = await postRequest("/api/connected-platforms/manual-token", {
        platform, accessToken: accessToken.trim(), adAccountId: adAccountId.trim(),
      });
      toast.success(`${STYLE[platform]?.label} connecté : ${res.accountName || res.accountId}`);
      setTokenForm({ ...tokenForm, accessToken: "", adAccountId: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Connexion refusée par la plateforme.");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
      {/* ── Synchronisation à la demande ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <p className="text-sm font-bold text-[#221C51]">🔄 Synchronisation des campagnes</p>
          <button
            onClick={() => runSync(null)}
            disabled={busy !== null}
            className="text-xs font-semibold text-white bg-[#221C51] rounded-xl px-3.5 py-2 hover:bg-[#2d2570] disabled:opacity-50 transition"
          >
            Tout synchroniser
          </button>
        </div>
        {status.platforms.length === 0 ? (
          <p className="text-xs text-gray-400 py-3">Aucune régie connectée pour l'instant.</p>
        ) : (
          <div className="space-y-2">
            {status.platforms.map((p) => {
              const s = STYLE[p.platform] || {};
              return (
                <div key={p.platform} className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-100 bg-gray-50">
                  <span className="w-7 h-7 rounded-lg flex items-center justify-center text-xs flex-shrink-0"
                    style={{ backgroundColor: s.bg, color: s.fg }}>{s.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#221C51]">{s.label || p.platform}</p>
                    <p className="text-[10px] text-gray-400 truncate">
                      {p.running ? "Synchronisation en cours…"
                        : p.error ? <span className="text-red-500">{p.error}</span>
                        : `Dernière : ${fmtDate(p.lastSync)}${p.counts ? ` · ${p.counts.campaigns} campagnes` : ""}`}
                    </p>
                  </div>
                  <button
                    onClick={() => runSync(p.platform)}
                    disabled={p.running || busy !== null}
                    title="Synchroniser maintenant"
                    className="text-xs font-semibold text-[#221C51] border border-[#221C51]/30 rounded-lg px-2.5 py-1.5 hover:bg-[#221C51]/5 disabled:opacity-40 transition flex items-center gap-1.5"
                  >
                    <FaSyncAlt className={p.running ? "animate-spin" : ""} /> {p.running ? "…" : "Sync"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Connexion par token (alternative à l'OAuth) ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 md:p-5">
        <p className="text-sm font-bold text-[#221C51] mb-1">🔑 Connexion par token</p>
        <p className="text-[11px] text-gray-500 leading-relaxed mb-3">
          Alternative si le bouton OAuth ne fonctionne pas (app indisponible, compte agence…).
          Générez un token dans votre régie et collez-le ici : il est validé en direct, chiffré,
          et reste révocable de votre côté à tout moment.
        </p>

        <div className="flex gap-1.5 flex-wrap mb-3">
          {TOKEN_PLATFORMS.map((p) => {
            const s = STYLE[p];
            const active = tokenForm.platform === p;
            return (
              <button key={p} onClick={() => setTokenForm({ ...tokenForm, platform: p })}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border flex items-center gap-1.5 transition ${
                  active ? "text-white border-transparent" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
                style={active ? { backgroundColor: s.bg, color: s.fg } : undefined}>
                {s.icon} {s.label}
              </button>
            );
          })}
        </div>

        <div className="space-y-2">
          <input
            value={tokenForm.adAccountId}
            onChange={(e) => setTokenForm({ ...tokenForm, adAccountId: e.target.value })}
            placeholder={tokenForm.platform === "meta" ? "act_123456789" : "ID du compte publicitaire"}
            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]"
          />
          <div className="relative">
            <input
              type={showToken ? "text" : "password"}
              value={tokenForm.accessToken}
              onChange={(e) => setTokenForm({ ...tokenForm, accessToken: e.target.value })}
              placeholder="Access token"
              autoComplete="off"
              className="w-full px-3 py-2.5 pr-16 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]"
            />
            <button type="button" onClick={() => setShowToken((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-400 hover:text-[#221C51] px-1.5">
              {showToken ? "masquer" : "voir"}
            </button>
          </div>
          <button
            onClick={connectToken}
            disabled={connecting || !tokenForm.accessToken.trim() || !tokenForm.adAccountId.trim()}
            className="w-full py-2.5 text-sm font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d2570] disabled:opacity-40 transition"
          >
            {connecting ? "Validation auprès de la plateforme…" : "Valider et connecter"}
          </button>
          <p className="text-[10px] text-gray-400 leading-relaxed">{TOKEN_HELP[tokenForm.platform]}</p>
        </div>
      </div>
    </div>
  );
};

export default SyncAndTokenPanel;
